import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Card,
  CardBody,
  CardImg,
  CardTitle,
} from "reactstrap";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, A11y } from "swiper/modules";
import { useHistory } from "react-router-dom";
import "swiper/css";
import "swiper/css/pagination";
import { toast } from "react-toastify";
import {
  addCartDetails,
  clearCartAction,
  clearCartDetails,
} from "../../store/actions/cartAction";

const useWindowWidth = () => {
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return width;
};

const OffersCarousel = ({ isHoliday }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const color = useSelector((state) => state.global.color);
  const { cartDetails } = useSelector((state) => state.cart);
  const [loadingOfferId, setLoadingOfferId] = useState(null);
  const { offers } = useSelector((state) => state.parentMenu);
  const { restaurantsDetail } = useSelector((state) => state.restaurants);
  const width = useWindowWidth();
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const shouldShowPagination =
    (width < 576 && offers.length > 1) ||
    (width >= 576 && width < 768 && offers.length > 2) ||
    (width >= 768 && width < 1200 && offers.length > 2) ||
    (width >= 1200 && offers.length > 3);

  const handleAddtoCartBtn = async (offer, offerId) => {
    if (isHoliday?.isHoliday || isHoliday?.storeTiming) {
      const typeLabel =
        isHoliday?.type === "dinein"
          ? "Dine-in"
          : isHoliday?.type === "delivery"
          ? "Delivery"
          : "Takeaway";

      const typeIcon =
        isHoliday?.type === "dinein"
          ? "🍽️"
          : isHoliday?.type === "delivery"
          ? "🚚"
          : "🛍️";

      toast.warning(
        isHoliday?.isHoliday
          ? `📅 Attention: The ${restaurantsDetail?.name} store is closed today due to ${isHoliday?.title}. Please check back tomorrow.`
          : `${typeIcon} Notice: ${typeLabel} service is currently unavailable as operating hours have ended. Please select a different option or check back tomorrow.`,
        {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          style: {
            backgroundColor: "#fff3cd",
            color: "#856404",
            fontWeight: "500",
            fontSize: "15px",
          },
        }
      );

      if (isHoliday?.storeTiming) {
        history.push("/order-options");
        dispatch(clearCartAction());
      } else if (isHoliday?.isHoliday) {
        history.push("/restaurants");
        dispatch(clearCartAction());
        dispatch(clearCartDetails());
      }
      return;
    }
    if (!isMountedRef.current) return;
    setLoadingOfferId(offerId);
    const updatedCart = { ...(cartDetails || {}) };
    dispatch(addCartDetails(updatedCart));
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));
      if (!isMountedRef.current) return;
      history.push({
        pathname: `/offerDetails/${offerId}`,
        state: { offer },
      });
    } finally {
      if (isMountedRef.current) {
        setLoadingOfferId(null);
      }
    }
  };

  useEffect(() => {
    const updateBulletColor = () => {
      const activeBullet = document.querySelector(
        ".swiper-pagination-bullet-active"
      );
      if (activeBullet) {
        activeBullet.style.backgroundColor = color;
      }
    };

    updateBulletColor();
    const observer = new MutationObserver(updateBulletColor);
    const pagination = document.querySelector(".swiper-pagination");
    if (pagination) {
      observer.observe(pagination, {
        childList: true,
        subtree: true,
        attributes: true,
      });
    }

    return () => {
      observer.disconnect();
    };
  }, [color, offers]);

  return (
    <div
      className="d-flex justify-content-center py-5"
      style={{ position: "relative" }}
    >
      <div style={{ maxWidth: "1200px", width: "100%", padding: "0 15px" }}>
        <Swiper
          modules={[Pagination, A11y]}
          pagination={
            shouldShowPagination
              ? {
                  clickable: true,
                  el: ".swiper-pagination",
                  bulletClass: "swiper-pagination-bullet",
                  bulletActiveClass: "swiper-pagination-bullet-active",
                }
              : false
          }
          grabCursor={true}
          speed={600}
          slidesPerView={1.2}
          spaceBetween={12}
          breakpoints={{
            576: {
              slidesPerView: 1.4,
              spaceBetween: 16,
            },
            768: {
              slidesPerView: 2,
              spaceBetween: 20,
            },
            1200: {
              slidesPerView: offers?.length >= 3 ? 3 : offers?.length,
              spaceBetween: 24,
            },
          }}
        >
          {offers.map((offer, index) => (
            <SwiperSlide key={index} className="d-flex justify-content-center">
              <Card
                className="shadow-sm"
                style={{
                  borderRadius: "1rem",
                  maxWidth: "320px",
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  minHeight: "480px",
                  overflow: "hidden",
                  backgroundColor: "#fff",
                  border: "1px solid #f0f0f0",
                }}
              >
                <CardImg
                  top
                  src={offer?.image}
                  alt={offer?.title}
                  style={{
                    height: "180px",
                    objectFit: "cover",
                    borderTopLeftRadius: "1rem",
                    borderTopRightRadius: "1rem",
                    borderBottom: "1px solid #eee",
                  }}
                />
                <CardBody
                  className="d-flex flex-column"
                  style={{
                    padding: "1rem",
                    flex: "1",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div>
                    <CardTitle
                      tag="h5"
                      className="text-dark fw-bold text-capitalize mb-3"
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        borderBottom: "1px solid #eee",
                        textOverflow: "ellipsis",
                        fontSize: "1.1rem",
                        minHeight: "3.3em",
                        lineHeight: "1.1rem",
                      }}
                    >
                      {offer?.title}
                    </CardTitle>

                    <div
                      style={{
                        maxHeight: "100px",
                        overflowY: "auto",
                        marginBottom: "1rem",
                        paddingRight: "0.5rem",
                        paddingTop: "0.5rem",
                      }}
                    >
                      {offer?.items?.map((item, idx, array) => (
                        <div
                          key={idx}
                          className="d-flex align-items-center mb-2"
                          style={{
                            borderBottom:
                              idx < array?.length - 1
                                ? "1px dashed #e0e0e0"
                                : "none",
                            paddingBottom: "6px",
                            minHeight: "60px",
                          }}
                        >
                          <img
                            src={item?.image}
                            alt={item?.itemName}
                            style={{
                              width: "45px",
                              height: "45px",
                              objectFit: "cover",
                              borderRadius: "0.5rem",
                              marginRight: "10px",
                              boxShadow: "0 0 4px rgba(0,0,0,0.05)",
                            }}
                          />
                          <div className="flex-grow-1">
                            <div className="fw-semibold text-dark text-truncate">
                              {item?.itemName}
                            </div>
                            <div className="small text-muted">
                              {item?.variant && `Variant: ${item.variant}`}
                            </div>
                            <div className="small text-muted">
                              {item?.quantity && `Quantity: x${item.quantity}`}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div
                    style={{
                      marginTop: "auto",
                      borderTop: "1px solid #eee",
                      paddingTop: "0.75rem",
                    }}
                  >
                    <div className="mb-2 fw-bold text-dark">
                      Price:{" "}
                      <span style={{ color: "#333" }}>
                        ${Number(offer?.price)}
                      </span>
                    </div>
                    <Button
                      style={{
                        background: color,
                        borderRadius: "0.5rem",
                        padding: "0.5rem",
                        fontWeight: 500,
                        fontSize: "0.95rem",
                      }}
                      className="w-100 border-0"
                      onClick={() => handleAddtoCartBtn(offer, offer?.id)}
                      disabled={loadingOfferId === offer.id}
                    >
                      Add to Cart
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </SwiperSlide>
          ))}
        </Swiper>
        {shouldShowPagination && (
          <div
            className="swiper-pagination"
            style={{ position: "relative", marginTop: "20px" }}
          ></div>
        )}
      </div>
    </div>
  );
};

export default OffersCarousel;
